import { Injectable, inject } from '@angular/core';
import { EncryptionService } from './encryption.service';
import { BrowserCacheLocation, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

export interface MsalTenantConfig {
  appid: string;
  tenid: string;
  db?: string;
  email?: string;
}

@Injectable({ providedIn: 'root' })
export class MsalConfigService {
  private encryptAes = inject(EncryptionService);

  private msalInstance: IPublicClientApplication | null = null;
  private clientId: string | null = null;
  private tenantId: string | null = null;

  async setConfig(tenantId: string, clientId: string): Promise<IPublicClientApplication> {
    const needsNewInstance = this.clientId !== clientId || this.tenantId !== tenantId;

    if (!needsNewInstance && this.msalInstance) {
      return this.msalInstance;
    }

    this.clientId = clientId;
    this.tenantId = tenantId;
    this.msalInstance = this.createInstance(this.tenantId, this.clientId);

    await this.msalInstance.initialize();
    return this.msalInstance;
  }

  getCurrentInstance(): IPublicClientApplication | null {
    return this.msalInstance;
  }

  async getInstance(): Promise<IPublicClientApplication | null> {
    if (!this.msalInstance) {
      await this.restoreFromLocalStorage();
    }

    return this.msalInstance;
  }

  getStoredConfig(): MsalTenantConfig | null {
    const encryptedMsal = localStorage.getItem('msal');
    if (!encryptedMsal) return null;

    try {
      const msal = JSON.parse(this.encryptAes.decryptionAES(encryptedMsal)) as MsalTenantConfig;
      if (!msal?.appid || !msal?.tenid) return null;
      return msal;
    } catch (error) {
      console.error('Failed to parse or decrypt msal localStorage data', error);
      localStorage.removeItem('msal');
      return null;
    }
  }

  persistConfig(config: MsalTenantConfig): void {
    localStorage.setItem('msal', this.encryptAes.encryptionAES(JSON.stringify(config)));
  }

  async clearMsalCache(): Promise<void> {
    sessionStorage.removeItem('msal.interaction.status');

    if (!this.msalInstance) return;

    try {
      this.msalInstance.setActiveAccount(null);
      await this.msalInstance.clearCache();
    } catch (error) {
      console.warn('[MSAL] Could not clear MSAL cache', error);
    }
  }

  clearRuntimeConfig(): void {
    this.msalInstance = null;
    this.clientId = null;
    this.tenantId = null;
  }

  getRedirectUri(): string {
    return environment.msalConfig.redirectUri;
  }

  private async restoreFromLocalStorage(): Promise<void> {
    const msal = this.getStoredConfig();
    if (!msal) return;

    this.clientId = msal.appid;
    this.tenantId = msal.tenid;
    this.msalInstance = this.createInstance(this.tenantId, this.clientId);

    await this.msalInstance.initialize();
  }

  private createInstance(tenantId: string, clientId: string): IPublicClientApplication {
    return new PublicClientApplication({
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: this.getRedirectUri(),
      },
      cache: {
        cacheLocation: BrowserCacheLocation.SessionStorage,
      },
      system: {
        allowPlatformBroker: false,
        navigatePopups: false,
        loggerOptions: {
          loggerCallback: (_, message) => console.log(message),
          logLevel: LogLevel.Info,
          piiLoggingEnabled: false,
        },
      },
    });
  }

  async isInitialized(): Promise<boolean> {
    return !!(await this.getInstance());
  }

  getCurrentClientId(): string | null {
    return this.clientId;
  }

  getCurrentTenantId(): string | null {
    return this.tenantId;
  }
}
